import { useState } from "react";
import PageSEO from "@/components/PageSEO";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";

const Contact = () => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    type: "suggestion",
    message: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    if (!form.message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        throw new Error("Failed to submit");
      }

      setForm({ name: "", email: "", type: "suggestion", message: "" });
      toast.success("Thank you for contacting us — we'll get back to you soon.");
    } catch (err) {
      toast.error("Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#f5f2ec" }}>
      <PageSEO
        title="Contact EV Hub Studio — Get in Touch"
        description="Contact the EV Hub Studio team for questions about EV charging station planning, partnerships or product feedback."
        path="/contact"
      />
      <header className="backdrop-blur sticky top-0 z-40" style={{ backgroundColor: "#504136", borderBottom: "1px solid #5e4e41" }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-14">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-2 transition-colors text-sm"
              style={{ color: "#B2E6D4" }}
              onMouseEnter={e => e.currentTarget.style.color = "#faf9f6"}
              onMouseLeave={e => e.currentTarget.style.color = "#B2E6D4"}
            >
              ← Back
            </button>
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff" }}>
              <CardHeader>
                <CardTitle className="text-xl" style={{ color: "#504136" }}>Contact EV Hub Studio</CardTitle>
                <CardDescription style={{ color: "#4a7068" }}>
                  Leave a review, suggestion, or enquiry. Anyone can submit this form.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label style={{ color: "#504136" }}>Name (optional)</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Your name"
                        style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label style={{ color: "#504136" }}>Email <span className="text-red-500">*</span></Label>
                      <Input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="you@example.com"
                        style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label style={{ color: "#504136" }}>Type</Label>
                    <Select
                      value={form.type}
                      onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}
                    >
                      <SelectTrigger style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }}>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="review">Review</SelectItem>
                        <SelectItem value="suggestion">Suggestion</SelectItem>
                        <SelectItem value="enquiry">Enquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label style={{ color: "#504136" }}>Message <span className="text-red-500">*</span></Label>
                    <Textarea
                      required
                      value={form.message}
                      onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                      placeholder="Write your message..."
                      rows={6}
                      style={{ borderColor: "#B2E6D4", backgroundColor: "#ffffff", color: "#504136" }}
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={submitting}
                      style={{ backgroundColor: "#689689", color: "#faf9f6" }}
                      onMouseEnter={e => { if (!e.currentTarget.disabled) { e.currentTarget.style.backgroundColor = "#83E8BA"; e.currentTarget.style.color = "#504136"; } }}
                      onMouseLeave={e => { if (!e.currentTarget.disabled) { e.currentTarget.style.backgroundColor = "#689689"; e.currentTarget.style.color = "#faf9f6"; } }}
                    >
                      {submitting ? "Sending..." : "Send"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Contact;
